import { charmsEventAriaText, eventTypeLabelForOOF } from './QuickComposePivots.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import { observer } from 'mobx-react-lite';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import { Pivot, PivotItem, PivotLinkFormat, PivotLinkSize } from '@fluentui/react/lib/Pivot';

import {
    updateQuickComposePivotContent,
    setQuickComposeActivePivot,
} from '../actions/internalActions';
import type { ComposeFormStore } from 'owa-calendar-compose-types';
import EventContent from './EventContent';
import { LazyOOFContent } from '../contentsIndex';
import { QuickComposePivotType } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import getQuickComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getQuickComposeViewState';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import QuickComposeContent from './QuickComposeContent';
import { isConsumer } from 'owa-session-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import type { IButtonProps } from '@fluentui/react/lib/Button';

const styles = require('./QuickComposePivots.scss');

const onPivotItemClicked = (onSizeUpdate: () => void) => {
    return (item: PivotItem) => {
        const previousPivot = getQuickComposeViewState().activePivot;
        const activePivot = item.props.itemKey as QuickComposePivotType;
        if (previousPivot != activePivot) {
            onSizeUpdate();
            setQuickComposeActivePivot(activePivot);
            updateQuickComposePivotContent(activePivot, previousPivot);
        }
    };
};

export interface QuickComposePivotsProps {
    state: ComposeFormStore;
    colorScheme: ColorScheme;
    calendarBarButtonProps: IButtonProps[];
    onSizeUpdate: () => void;
}

const QuickComposePivots = observer(function QuickComposePivots(props: QuickComposePivotsProps) {
    const { state, colorScheme, onSizeUpdate, calendarBarButtonProps } = props;
    const { event } = state;
    const viewState = getQuickComposeViewState();

    if (!event || !viewState) {
        return null;
    }

    const calendarTheme = themeFromColorScheme(colorScheme);
    const showOOF =
        isFeatureEnabled('cal-multiOOF-quickCompose') &&
        !getUserConfiguration().SessionSettings.IsShadowMailbox;

    return (
        <Pivot
            styles={{ root: styles.rootContainer }}
            theme={calendarTheme}
            linkFormat={PivotLinkFormat.links}
            linkSize={PivotLinkSize.normal}
            defaultSelectedKey={viewState.activePivot}
            onLinkClick={onPivotItemClicked(onSizeUpdate)}>
            <PivotItem itemKey={QuickComposePivotType.Event} headerText={loc(charmsEventAriaText)}>
                {!isConsumer() ? (
                    <EventContent
                        event={event}
                        colorScheme={colorScheme}
                        calendarBarButtonProps={calendarBarButtonProps}
                    />
                ) : (
                    <QuickComposeContent
                        state={state}
                        colorScheme={colorScheme}
                        calendarBarButtonProps={calendarBarButtonProps}
                        onSizeUpdate={onSizeUpdate}
                    />
                )}
            </PivotItem>
            {showOOF && (
                <PivotItem
                    itemKey={QuickComposePivotType.OOF}
                    headerText={loc(eventTypeLabelForOOF)}>
                    <LazyOOFContent colorScheme={colorScheme} onSizeUpdate={onSizeUpdate} />
                </PivotItem>
            )}
        </Pivot>
    );
});

export default QuickComposePivots;
